import {useModule, useSelectors, useSelectorWithRefresh} from '../kysely_module';

import {useParams} from 'react-router';

import springboard from 'springboard';
import {StateSupervisor} from 'springboard/services/states/shared_state_service';
import {DBObjects} from '../store_types';

import type {WithCallOptions} from '@/store/kysely_module';
import {useEffect, useMemo, useRef, useState} from 'react';

declare module 'springboard/module_registry/module_registry' {
    interface AllModules {
        Workspaces: WorkspacesModuleReturnValue;
    }
}

type WorkspacesModuleReturnValue = {
    currentWorkspaceIdState: StateSupervisor<string>;
    setCurrentWorkspaceId: (workspaceId: string) => void;
    knownWorkspacesState: StateSupervisor<KnownWorkspace[]>;
    setKnownWorkspaces: (knownWorkspaces: KnownWorkspace[]) => void;
};

type KnownWorkspace = {
    workspace: DBObjects['Workspace'];
    type: 'remote' | 'local';
}

const initialWorkspaceId = '';

export default (sb: typeof springboard) => sb.registerModule('Workspaces', {}, async (moduleAPI): Promise<WorkspacesModuleReturnValue> => {
    const currentWorkspaceIdState = await moduleAPI.statesAPI.createUserAgentState('currentWorkspaceIdState', initialWorkspaceId);
    const knownWorkspacesState = await moduleAPI.statesAPI.createUserAgentState<KnownWorkspace[]>('knownWorkspaces', []);

    const setKnownWorkspaces = (knownWorkspaces: KnownWorkspace[]) => {
        knownWorkspacesState.setState(knownWorkspaces);
    };

    const setCurrentWorkspaceId = (workspaceId: string) => {
        currentWorkspaceIdState.setState(workspaceId);
    };

    // const kyselyModule = moduleAPI.deps.module.moduleRegistry.getModule('Kysely');
    // if (!currentWorkspaceIdState.getState()) {
    //     const workspaces = await kyselyModule.selectors.getAllWorkspaces();
    //     if (workspaces.length) {
    //         currentWorkspaceIdState.setState(workspaces[0].id);
    //     }
    // }

    return {
        currentWorkspaceIdState,
        setCurrentWorkspaceId,
        setKnownWorkspaces,
        knownWorkspacesState,
    };
});

export const useCurrentWorkspaceId = () => {
    const mod = useModule('Workspaces');
    const currentState = mod.currentWorkspaceIdState.useState();

    const params = useParams();
    if (params.workspaceId) {
        return params.workspaceId;
    }

    return currentState;
};

export const useCurrentProjectId = () => {
    const params = useParams();
    if (params.projectId) {
        return params.projectId;
    }

    return undefined;
};

export const useCurrentFileId = () => {
    const params = useParams();
    if (params.fileId) {
        return params.fileId;
    }

    return undefined;
};

type CurrentObjects = {
    workspace: DBObjects['Workspace'] | null;
    project: DBObjects['Project'] | null;
    file: DBObjects['File'] | null;
    obj: {id: string; name: string; type: string} | null;
    refresh: () => void;
    breadcrumbPairs: {
        id: string;
        name: string;
        url: string;
    }[];
}

export const useCurrentObjects = (): CurrentObjects => {
    const workspaceId = useCurrentWorkspaceId();
    const projectId = useCurrentProjectId();
    const fileId = useCurrentFileId();

    const [workspace] = useSelectorWithRefresh(
        'getWorkspace',
        {workspace_id: workspaceId || ''},
        null,
        {workspace_id: null, ignore: !workspaceId},
    );

    const [project, refreshProject] = useSelectorWithRefresh(
        'getProject',
        {project_id: projectId || '', includeHidden: true},
        null,
        {workspace_id: workspaceId, ignore: !projectId},
    );

    const [file, refreshFile] = useSelectorWithRefresh(
        'getFileInfo',
        {file_id: fileId || ''},
        null,
        {workspace_id: workspaceId, ignore: !fileId},
    );

    const [fileWithProject, refreshFileWithProject] = useSelectorWithRefresh(
        'getBreadcrumbInfoForFile',
        {file_id: fileId || ''},
        null,
        {workspace_id: workspaceId, ignore: !fileId},
    );

    const breadcrumbPairs = useMemo(() => {
        const pairs: {id: string; name: string; url: string}[] = [];
        if (workspaceId) {
            // pairs.push({
            //     id: workspaceId,
            //     name: workspace?.name || 'Workspace',
            //     url: `/workspace/${workspaceId}`,
            // });
        }
        if (projectId) {
            pairs.push({
                id: projectId,
                name: project?.name || '',
                url: `/workspaces/${workspaceId}/projects/${projectId}`,
            });
        }
        if (fileId) {
            if (fileWithProject && !projectId) {
                pairs.push({
                    id: fileWithProject.project.id,
                    name: fileWithProject.project.name,
                    url: `/workspaces/${workspaceId}/projects/${fileWithProject.project.id}`,
                });
            }
            pairs.push({
                id: fileId,
                name: file?.name || '',
                url: `/workspaces/${workspaceId}/files/${fileId}`,
            });
        }
        return pairs;
    }, [workspaceId, projectId, fileId, workspace, project, file, fileWithProject]);

    let obj: {id: string; name: string; type: string} | null = null;

    if (fileId && file) {
        obj = {
            id: fileId,
            name: file.name,
            type: 'file',
        };
    }

    if (projectId && project) {
        obj = {
            id: projectId,
            name: project.name,
            type: 'project',
        };
    }

    return {
        obj,
        refresh: () => {
            refreshProject();
            refreshFile();
            refreshFileWithProject();
        },
        breadcrumbPairs,
        workspace: workspace || null,
        project: fileWithProject?.project || project || null,
        file: file || null,
    };
};

export const useEnsureCurrentWorkspaceId = () => {
    const mod = useModule('Workspaces');

    const currentWorkspaceId = mod.currentWorkspaceIdState.useState();
    const getAllWorkspaces = useSelectors().getAllWorkspaces;

    useEffect(() => {
        if (currentWorkspaceId) {
            return;
        }

        (async () => {
            const remoteWorkspaces = await getAllWorkspaces({}, {mode: 'remote'}).catch(() => null);
            const localWorkspaces = await getAllWorkspaces({}, {mode: 'local'}).catch(() => null);
            const workspaces = (remoteWorkspaces || []).concat(localWorkspaces || []);

            if (workspaces.length) {
                mod.setCurrentWorkspaceId(workspaces[0].id);

                const knownWorkspaces = workspaces.map(w => ({
                    workspace: w,
                    type: w.type === 'LOCAL' ? ('local' as const) : ('remote' as const),
                }));

                mod.setKnownWorkspaces(knownWorkspaces);
            }
        })();
    }, [currentWorkspaceId]);
};
